import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Typography, Box, Skeleton, Grid } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import EMPTabs from "../../shared/components/EMPTabs/EMPTabs";
import EMPChip from "../../shared/components/EMPChip/EMPChip";
import EMPBreadcrumbs from "../../shared/components/EMPBreadcrumbs/EMPBreadcrumbs";
import ActionCardGrid from "./ActionCardGrid";
import CustomerInfo from "./CustomerInfo";
import Vehicle from "./Vehicle";
import Pricing from "./Pricing";
import Financing from "./Financing";
import TradeIn from "./TradeIn";
import NewDelivery from "./NewDelivery";
import { useLocation, useParams } from "react-router-dom";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPCounter from "../../shared/components/EMPCounter/EMPCounter";
import {
  DrawerProvider,
  DrawerContext,
} from "../../shared/components/EMPDrawer/DrawerContext";
import { ModalProvider } from "../../shared/components/EMPModal/ModalContext";
import OrderNotifications from "./OrderNotifications";
import { useQuery } from "@tanstack/react-query";
import NADealerNotificationsClient from "../../clients/NADealerNotificationsClient";
import DealerNotes from "./DealerNotes";
import { UserContext } from "../../shared/context/userContext";
import DealerNotesClient from "../../clients/DealerNotesClient";
import {
  getPurchaseRequest,
  isPurchaseRequestAccepted,
  isPurchaseRequestCanceled,
} from "../../shared/rules/PurchaseRequestRules";
import { useOrder } from "../../shared/hooks/useOrder";
import { canViewNotifications } from "../../shared/rules/NotificationRules";
import { useTranslation } from "react-i18next";
import EMPBlankSection from "../../shared/components/EMPBlankSection/EMPBlankSection";
import {
  trackOrderDetailsPageAction,
  trackOrderDetailsPageLoad,
} from "../../shared/services/analytics/OrderDetailsPageAnalytics";
import { canViewNotes } from "../../shared/rules/DealerNotesRules";
import { useCrcStatus } from "../../shared/hooks/useCrcStatus";
import { useVehicleData } from "../../shared/hooks/useVehicleData";
import withIdleTimer from "../../common/WithIdleTimer";
import PreOrderActionBanner from "./preorders/PreOrderActionBanner";
import PreOrderTabs from "./preorders/PreOrderTabs";
import { usePreOrderStatus } from "./preorders/preOrderStatusStore";
import mockPreOrderPricingSummary from "../../clients/mocks/uevPreOrderPricingSummary.json";

const LINCOLN_INTERACTIVE_COLOR = "#485B65";

const UEV_JOURNEY_TYPE = "UEV";

const LINCOLN_ORDER_STATUS_CHIPS = {
  accepted: { labelKey: "common.actionStatus.accepted", color: "#26732B" },
  canceled: { labelKey: "common.actionStatus.canceled", color: "#D62D14" },
};

const LincolnOrderStatusChip = ({ purchaseInfo }) => {
  const { t } = useTranslation();
  let chip = null;
  if (isPurchaseRequestAccepted(purchaseInfo)) {
    chip = LINCOLN_ORDER_STATUS_CHIPS.accepted;
  } else if (isPurchaseRequestCanceled(purchaseInfo)) {
    chip = LINCOLN_ORDER_STATUS_CHIPS.canceled;
  }

  if (!chip) {
    return null;
  }

  return (
    <Box
      component="span"
      data-testid="order-status-chip"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 3,
        py: 1,
        border: `1px solid ${chip.color}`,
        borderRadius: "4px",
        backgroundColor: "#FFFFFF",
        color: chip.color,
        fontFamily: "ProximaNovaRegular, Arial, sans-serif",
        fontSize: 16,
        lineHeight: "24px",
        whiteSpace: "nowrap",
      }}
    >
      {t(chip.labelKey)}
    </Box>
  );
};

LincolnOrderStatusChip.propTypes = {
  purchaseInfo: PropTypes.shape({
    acceptRejectStatus: PropTypes.string,
  }),
};

LincolnOrderStatusChip.defaultProps = {
  purchaseInfo: null,
};

const PRE_ORDER_CANCELED_CHIP_SX = {
  height: "auto",
  px: 2,
  py: 0,
  border: "1px solid #EA959B",
  borderRadius: "4px",
  backgroundColor: "#FFFFFF",
  "& .MuiChip-label": {
    color: "#BF152C",
    fontSize: "16px",
    lineHeight: "24px",
    padding: 0,
  },
};

const PreOrderCanceledPill = ({ label }) => (
  <EMPChip
    testId="preorder-canceled-pill"
    variant="outlined"
    label={label}
    sx={PRE_ORDER_CANCELED_CHIP_SX}
  />
);

PreOrderCanceledPill.propTypes = {
  label: PropTypes.string.isRequired,
};

const OrderDetailsHeader = ({ orderId, isPreOrder }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { market, dealerId, salesCode, brand } = user;
  const isLincolnBrand = brand === "L";
  const showDealerNotes = !isLincolnBrand;
  const userCommonId = `${market}|${dealerId}|${salesCode}|${brand}`;
  const hasNotificationsViewPermission = canViewNotifications();
  const hasNotesViewPermission = canViewNotes();
  const { nameplate, modelYear } = useVehicleData(orderId);

  const { data: notifications } = useQuery({
    queryKey: ["notifications", { orderId }],
    queryFn: () =>
      NADealerNotificationsClient.getNotifications(
        userCommonId.split("|").join("%7C"),
        orderId
      ),
    enabled: hasNotificationsViewPermission && !!orderId,
    staleTime: 60000,
    retries: 2,
  });

  const { data: notes } = useQuery({
    queryKey: ["dealerNotes", { orderId }],
    queryFn: () => DealerNotesClient.getDealerNotes(orderId, 0, 100),
    enabled: showDealerNotes && hasNotesViewPermission && !!orderId,
    staleTime: 60000,
    select: ({ data }) => data,
  });

  const { handleDrawer, drawerHeight } = useContext(DrawerContext);
  const totalNotificationsCount = notifications?.data?.pagination?.totalCount;
  const totalNotesCount = notes?.pagination?.totalNotes;
  const isPurchaseRequest = getPurchaseRequest(orderId);
  const preOrderStatus = usePreOrderStatus(orderId);
  const isPreOrderCanceled =
    preOrderStatus?.status?.toLowerCase() === "cancelled";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        alignItems: "center",
        gap: 4,
        mb: 4,
      }}
    >
      <Box gridColumn={{ xs: "span 12", sm: "span 12", md: "span 7" }}>
        <Grid
          container
          alignItems="center"
          justifyContent={{
            xs: "space-between",
            sm: "space-between",
            md: "flex-start",
          }}
        >
          <Grid item>
            <Typography variant="h1" sx={{ color: "text.primary" }}>
              {isPurchaseRequest
                ? `${t("common.orderTypes.purchase")} #${orderId}`
                : `${t("common.orderTypes.order")} #${orderId}`}
            </Typography>
          </Grid>
          {isPreOrder && isPreOrderCanceled && (
            <Grid item sx={{ ml: 4 }}>
              <PreOrderCanceledPill
                label={t("CustomerOrderDetails.preOrder.statusPill.canceled", {
                  defaultValue: "Pre-Order Canceled",
                })}
              />
            </Grid>
          )}
          {isLincolnBrand && !isPreOrder && (
            <Grid item sx={{ ml: 4 }}>
              <LincolnOrderStatusChip purchaseInfo={isPurchaseRequest} />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box
        gridColumn={{ xs: "span 12", sm: "span 12", md: "span 5" }}
        sx={{ display: "flex", justifyContent: "flex-end", gap: 6 }}
      >
        <EMPButton
          variant="text"
          color="primary"
          onClick={() => {
            handleDrawer(
              totalNotificationsCount
                ? `${t("common.notifications")} (${totalNotificationsCount})`
                : t("common.notifications"),
              hasNotificationsViewPermission ? (
                <OrderNotifications
                  notifications={notifications}
                  orderId={orderId}
                />
              ) : (
                "No permission"
              )
            );
            trackOrderDetailsPageAction(
              undefined,
              "notifications",
              orderId,
              modelYear,
              nameplate,
              user
            );
          }}
          sx={{ position: "relative", display: "flex", alignItems: "center" }}
        >
          <Box
            component="span"
            sx={{
              color: isLincolnBrand ? LINCOLN_INTERACTIVE_COLOR : "inherit",
            }}
          >
            {t("common.notifications")}
          </Box>
          {totalNotificationsCount > 0 && (
            <Box ml={2} sx={{ display: "inline-block" }}>
              <EMPCounter
                count={totalNotificationsCount}
                testId="notifications-counter"
              />
            </Box>
          )}
        </EMPButton>

        {showDealerNotes && (
          <EMPButton
            variant="text"
            color="primary"
            disabled={!hasNotesViewPermission}
            onClick={() =>
              handleDrawer(
                totalNotesCount
                  ? `${t("common.dealerNotes")} (${totalNotesCount})`
                  : t("common.dealerNotes"),
                <DealerNotes orderId={orderId} drawerHeight={drawerHeight} />
              )
            }
          >
            {t("common.dealerNotes")}
            {totalNotesCount > 0 && (
              <Box ml={2} sx={{ display: "inline-block" }}>
                <EMPCounter count={totalNotesCount} testId="notes-counter" />
              </Box>
            )}
          </EMPButton>
        )}
        <EMPButton
          variant={isLincolnBrand ? "text" : "outlined"}
          color="primary"
          rightIcon={
            isLincolnBrand ? (
              <PrintOutlinedIcon sx={{ fontSize: 20 }} />
            ) : undefined
          }
          sx={
            isLincolnBrand
              ? {
                  color: LINCOLN_INTERACTIVE_COLOR,
                  border: "none",
                  borderRadius: 0,
                  minWidth: 0,
                  px: 0,
                  "&:hover": { backgroundColor: "transparent" },
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${LINCOLN_INTERACTIVE_COLOR}`,
                    outlineOffset: 2,
                  },
                }
              : undefined
          }
          onClick={() => {
            trackOrderDetailsPageAction(
              undefined,
              "print",
              orderId,
              modelYear,
              nameplate,
              user
            );
            globalThis.print();
          }}
        >
          {t("common.print")}
        </EMPButton>
      </Box>
    </Box>
  );
};

OrderDetailsHeader.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isPreOrder: PropTypes.bool,
};

OrderDetailsHeader.defaultProps = {
  isPreOrder: false,
};

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const { user } = useContext(UserContext);
  const { market, dealerId, salesCode, brand } = user;
  const isLincolnBrand = brand === "L";
  const userCommonId = `${market}|${dealerId}|${salesCode}|${brand}`;
  const theme = useTheme();
  const orderDetailsTheme = useMemo(
    () =>
      isLincolnBrand
        ? createTheme(theme, {
            palette: {
              primary: {
                main: LINCOLN_INTERACTIVE_COLOR,
                dark: "#3A4A53",
              },
              info: { main: "#ECEEEF" },
            },
            components: {
              EMPButton: {
                styleOverrides: {
                  root: { borderRadius: 0 },
                },
              },
            },
          })
        : theme,
    [isLincolnBrand, theme]
  );
  const [activeTab, setActiveTab] = useState(0);
  const tabsRef = useRef(null);
  const { t } = useTranslation();
  const isCrc = useCrcStatus();

  const {
    isFetching,
    status,
    error,
    data: order,
  } = useOrder(userCommonId, orderId);

  const isPreOrder =
    order?.customerReservation?.journeyType === UEV_JOURNEY_TYPE;

  function clickToTab(tabNumber, analyticsStr) {
    trackOrderDetailsPageAction(
      undefined,
      analyticsStr,
      orderId,
      modelYear,
      nameplate,
      user
    );

    if (tabsRef.current) {
      globalThis.scrollTo({
        top: tabsRef.current.offsetTop,
        behavior: "smooth",
      });
    }

    setActiveTab(tabNumber);
  }

  function changeTab(tabValue) {
    setActiveTab(tabValue);
    const selectedTab = tabItems.find((tab) => tab.value === tabValue);
    const tabTitle = selectedTab?.title.toLowerCase();

    trackOrderDetailsPageAction(
      undefined,
      `tab:${tabTitle}`,
      orderId,
      modelYear,
      nameplate,
      user
    );
  }

  useEffect(() => {
    const lastTab = globalThis.sessionStorage.getItem("lastTab");
    const parsedTab = JSON.parse(lastTab);
    if (parsedTab && Object.hasOwn(parsedTab, orderId)) {
      setActiveTab(Number.parseInt(parsedTab[orderId].tabNumber, 10));
    }
  }, [orderId]);

  const { nameplate, modelYear } = useVehicleData(orderId);

  useEffect(() => {
    if (order) {
      trackOrderDetailsPageLoad(undefined, orderId, modelYear, nameplate, user);
    }
  }, [order, orderId, modelYear, nameplate, user]);

  const tabItems = [
    {
      title: t("common.categories.customer"),
      route: "customer-info",
      content: <CustomerInfo content={order} orderId={orderId} />,
      value: 0,
    },
    {
      title: t("common.categories.vehicle"),
      route: "vehicle",
      content: <Vehicle content={order} orderId={orderId} />,
      value: 1,
    },
    {
      title: t("common.categories.pricing"),
      route: "pricing",
      content: <Pricing content={order} orderId={orderId} />,
      value: 2,
    },
    {
      title: t("common.categories.financing"),
      route: "financing",
      content: <Financing content={order} orderId={orderId} />,
      value: 3,
    },
    {
      title: t("common.categories.tradeIn"),
      route: "trade-in",
      content: <TradeIn content={order} orderId={orderId} />,
      value: 4,
    },
    {
      title: t("common.categories.delivery"),
      route: "delivery",
      content: <NewDelivery orderId={orderId} content={order} />,
      value: 5,
    },
  ].filter((tab) => !isLincolnBrand || tab.route !== "delivery");

  // A saved Delivery selection is no longer available for Lincoln orders.
  const visibleActiveTab = tabItems.some((tab) => tab.value === activeTab)
    ? activeTab
    : 0;

  if (status === "error") {
    return (
      <>
        <Box
          component="section"
          sx={{ py: 4, px: { xs: 4, sm: 4, md: 4, lg: 14 } }}
        >
          <Box sx={{ mb: 4 }}>{!isCrc && <EMPBreadcrumbs t={t} />}</Box>

          <EMPBlankSection
            message1={`Something went wrong while loading ${orderId}`}
            message2={`${error.code}: ${error.response?.data?.detail}`}
          />
        </Box>
      </>
    );
  }

  return (
    <ThemeProvider theme={orderDetailsTheme}>
      <DrawerProvider>
        <ModalProvider>
          <Box
            component="section"
            sx={{ py: 4, px: { xs: 4, sm: 4, md: 4, lg: 14 } }}
          >
            <Box sx={{ mb: 4 }}>{!isCrc && <EMPBreadcrumbs t={t} />}</Box>
            {status === "loading" && (
              <Skeleton
                variant="rectangular"
                height={44}
                sx={{ mb: 4 }}
              ></Skeleton>
            )}
            {!isFetching && order && (
              <Box>
                <OrderDetailsHeader
                  orderId={orderId}
                  origin={
                    order?.customerReservation?.spotBuy ? "Spot Buy" : "Online"
                  }
                  isPreOrder={isPreOrder}
                />
              </Box>
            )}

            {isFetching ? (
              <Box
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gap={4}
                sx={{ mb: 4 }}
              >
                {[1, 2, 3].map((item) => {
                  return (
                    <Box
                      gridColumn={{
                        xs: "span 6",
                        sm: "span 6",
                        md: "span 4",
                        lg: "span 4",
                      }}
                      key={item}
                    >
                      <Skeleton variant="rectangular" height={240}></Skeleton>
                    </Box>
                  );
                })}
              </Box>
            ) : isPreOrder ? (
              <>
                <Box sx={{ mb: 6 }}>
                  <PreOrderActionBanner orderId={orderId} />
                </Box>
                <Box ref={tabsRef}>
                  <PreOrderTabs
                    order={order}
                    orderId={orderId}
                    activeTab={activeTab}
                    onTabChange={changeTab}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box
                  data-testid="order-actions-section"
                  sx={{
                    mb: 6,
                    ...(isLincolnBrand && { "&:empty": { display: "none" } }),
                  }}
                >
                  <ActionCardGrid
                    orderId={orderId}
                    handleTabChange={(num, analyticsStr) =>
                      clickToTab(num, analyticsStr)
                    }
                  ></ActionCardGrid>
                </Box>
                <Box ref={tabsRef}>
                  <EMPTabs
                    tabItems={tabItems}
                    activeTab={visibleActiveTab}
                    onTabChange={changeTab}
                    variant={isLincolnBrand ? "segmented" : "underlined"}
                  ></EMPTabs>
                </Box>
              </>
            )}
          </Box>
        </ModalProvider>
      </DrawerProvider>
    </ThemeProvider>
  );
};

const MockPreOrderDetailsPage = ({ orderId }) => {
  const [activeTab, setActiveTab] = useState(0);

  const mockOrder = {
    customerInfo: {
      firstName: "Alex",
      lastName: "Taylor",
      email: "alex.taylor@example.com",
      phoneNumber: "555-0100",
      address1: "123 Demo Street",
      city: "Dearborn",
      state: "MI",
      zip: "48126",
    },
    customerReservation: {
      journeyType: UEV_JOURNEY_TYPE,
      journey: { businessType: "PREORDER" },
      pricingSummary: mockPreOrderPricingSummary,
      trim: "Premium",
      color: "Glacier Gray",
      drivetrain: "AWD",
      battery: "Extended Range",
      wheels: "20-inch alloy wheels",
      accessories: ["All-weather floor mats", "Cargo liner"],
      elements: ["Panoramic roof"],
      subscriptions: ["Connected services"],
      chargingEquipment: ["Home charging station"],
      registrationMonth: 6,
      keepLicensePlateFlag: true,
    },
  };

  return (
    <PreOrderTabs
      order={mockOrder}
      orderId={orderId}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isMock
    />
  );
};

const SwitchOrderDetailsPage = () => {
  const { orderId } = useParams();
  const location = useLocation();

  // The table sends this history-state flag for PREORDER rows. Rendering this
  // branch avoids mounting the standard detail page and its backend queries.
  // PreOrderTabs supplies the breadcrumbs and title for this standalone view.
  return location.state?.mockPreOrder ? (
    <MockPreOrderDetailsPage orderId={orderId} />
  ) : (
    <OrderDetailsPage />
  );
};

export default withIdleTimer(SwitchOrderDetailsPage);
